/**
 * Cache Metrics Panel Component
 * Displays cache performance metrics (hit rate, hits/misses, latency)
 */

import { Card } from '@/components/ui/Card';
import { Database, TrendingUp, TrendingDown, Clock } from 'lucide-react';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/cn';
import type { CacheMetrics } from '@/types/monitoring';

export interface CacheMetricsPanelProps {
  metrics: CacheMetrics | null;
}

/**
 * Format percentage for display
 */
function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * Format number with thousands separator
 */
function formatNumber(value: number): string {
  return value.toLocaleString();
}

/**
 * Format latency for display
 */
function formatLatency(latency: number): string {
  if (latency < 1) return '<1ms';
  return `${Math.round(latency)}ms`;
}

/**
 * Get color based on hit rate performance
 */
function getHitRateColor(hitRate: number): string {
  if (hitRate >= 0.8) return 'text-success';
  if (hitRate >= 0.5) return 'text-warning';
  return 'text-destructive';
}

export function CacheMetricsPanel({ metrics }: CacheMetricsPanelProps) {
  const { messages } = useI18n();
  const t = messages.monitoring.cache;

  if (!metrics) {
    return (
      <Card className="border-info/20">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-info" />
          <h3 className="text-lg font-semibold text-foreground">{t.title}</h3>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <Database className="w-12 h-12 mx-auto mb-2" />
          <p>{t.unavailable}</p>
        </div>
      </Card>
    );
  }

  const hitRateColor = getHitRateColor(metrics.hit_rate);

  return (
    <Card className="border-info/20">
      <div className="flex items-center gap-2 mb-4">
        <Database className="w-5 h-5 text-info" />
        <h3 className="text-lg font-semibold text-foreground">{t.title}</h3>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Hit Rate */}
        <div className="bg-card rounded-lg p-4 border border-info/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">{t.hitRate}</span>
            <TrendingUp className={cn('w-4 h-4', hitRateColor)} />
          </div>
          <div className={cn('text-2xl font-bold', hitRateColor)}>
            {formatPercentage(metrics.hit_rate)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {formatNumber(metrics.cache_requests)} {t.cacheRequests}
          </div>
        </div>

        {/* Cache Hits */}
        <div className="bg-card rounded-lg p-4 border border-success/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">{t.hitEntries}</span>
            <TrendingUp className="w-4 h-4 text-success" />
          </div>
          <div className="text-2xl font-bold text-success">
            {formatNumber(metrics.hits)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {t.entriesFound}
          </div>
        </div>

        {/* Cache Misses */}
        <div className="bg-card rounded-lg p-4 border border-warning/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">{t.missEntries}</span>
            <TrendingDown className="w-4 h-4 text-warning" />
          </div>
          <div className="text-2xl font-bold text-warning">
            {formatNumber(metrics.misses)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {t.entriesNotFound}
          </div>
        </div>

        {/* Average Latency */}
        <div className="bg-card rounded-lg p-4 border border-info/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">{t.avgLatency}</span>
            <Clock className="w-4 h-4 text-info" />
          </div>
          <div className="text-2xl font-bold text-info">
            {formatLatency(metrics.avg_latency_ms)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {t.avgResponseTime}
          </div>
        </div>
      </div>
    </Card>
  );
}
